/**
 * PersonDetailsModal - Modal de detalhes de Pessoa (Fornecedores, Fabricantes, Prestadores,
 * Transportadoras e Pessoas).
 *
 * - Aba Dados (todos os papéis): somente leitura; botão Editar abre o formulário oficial.
 * - Prestador ganha as abas O.S (cards + `ServiceOrdersEmbeddedList` filtrada pelo prestador)
 *   e Movimentação (`ProviderMovementsTab`). Nada de listagem nova: tudo reaproveitado.
 * - Tela cheia no celular (padrão dos modais de detalhes).
 *
 * RELEASE: 2026-10-14 v1.1.0 - Aba Dados segue PF/PJ com as seções do formulário.
 * RELEASE: 2026-10-14 v1.0.0 - Versão inicial.
 */
import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, ClipboardList, Loader2, Pencil, Wrench } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { KpiCard } from '@/components/layout/list-page';
import { ServiceOrdersEmbeddedList } from '@/components/service-orders/ServiceOrdersEmbeddedList';
import { ProviderMovementsTab } from '@/components/persons/ProviderMovementsTab';
import { formatPersonDocument } from '@/components/persons/PersonFormDialog';
import { PERSON_ROLES, type Person } from '@/hooks/usePersons';
import { formatPhoneForDisplay } from '@/lib/phone-utils';
import type { ServiceOrderKpis } from '@/hooks/useServiceOrders';

interface Props {
  person: Person | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Abre o formulário oficial de edição (só quando o usuário pode editar). */
  onEdit?: (person: Person) => void;
}

/** "Em andamento" = agendada + em andamento + em revisão + em correção; "Concluídas" = concluídas. */
export function providerOrderCards(k: ServiceOrderKpis | null) {
  return {
    total: k?.total ?? 0,
    inProgress: (k?.scheduled ?? 0) + (k?.in_progress ?? 0) + (k?.awaiting_review ?? 0) + (k?.in_correction ?? 0),
    completed: k?.completed ?? 0,
  };
}

/** CEP 00000-000 só para exibição. */
export function formatCep(cep: string | null | undefined) {
  const d = (cep || '').replace(/\D/g, '');
  return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

/** Bloco com título (mesmas seções do formulário). */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <h4 className="mb-2 text-xs font-semibold text-foreground">{title}</h4>
      <div className="grid gap-2 sm:grid-cols-2">{children}</div>
    </div>
  );
}

/** Linha rotulada da aba Dados (oculta quando vazia). */
function Field({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div className="min-w-0 rounded-md bg-muted/40 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="truncate text-sm text-foreground" title={value != null ? String(value) : undefined}>{value || '—'}</div>
    </div>
  );
}

export function PersonDetailsModal({ person, open, onOpenChange, onEdit }: Props) {
  const [tab, setTab] = useState('data');
  const [kpis, setKpis] = useState<ServiceOrderKpis | null>(null);
  const onKpis = useCallback((k: ServiceOrderKpis) => setKpis(k), []);
  useEffect(() => { if (open) { setTab('data'); setKpis(null); } }, [open, person?.id]);

  if (!person) return null;
  const isProvider = person.roles.includes('service_provider');
  const cards = providerOrderCards(kpis);
  const isPJ = person.person_type === 'PJ';
  const cityState = [person.city, person.state].filter(Boolean).join(' / ');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[100dvh] max-h-[100dvh] w-full max-w-full flex-col gap-0 overflow-hidden p-0 sm:h-[85dvh] sm:max-w-5xl sm:rounded-lg">
        {/* Cabeçalho compacto */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b px-5 py-4 pr-12">
          <DialogHeader className="min-w-0 space-y-1 text-left">
            <DialogTitle className="truncate text-base">{person.name}</DialogTitle>
            <DialogDescription className="flex flex-wrap items-center gap-1.5 text-xs">
              {formatPersonDocument(person.document) || 'Sem documento'}
              {person.roles.map((r) => (
                <Badge key={r} variant="secondary" className="h-5 px-1.5 text-[10px] font-medium">{PERSON_ROLES.find((x) => x.value === r)?.label ?? r}</Badge>
              ))}
              <Badge variant={person.is_active ? 'outline' : 'secondary'} className="h-5 px-1.5 text-[10px]">{person.is_active ? 'Ativo' : 'Inativo'}</Badge>
            </DialogDescription>
          </DialogHeader>
          {onEdit && (
            <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => onEdit(person)}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" />Editar
            </Button>
          )}
        </div>

        <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
          <TabsList className="mx-4 mt-3 w-fit shrink-0 overflow-x-auto">
            <TabsTrigger value="data">Dados</TabsTrigger>
            {isProvider && <TabsTrigger value="orders">O.S{kpis ? <span className="ml-1 text-[10px] text-muted-foreground">{cards.total}</span> : null}</TabsTrigger>}
            {isProvider && <TabsTrigger value="movements">Movimentação</TabsTrigger>}
          </TabsList>

          <TabsContent value="data" className="mt-0 min-h-0 flex-1 overflow-y-auto p-4">
            {/* v1.1.0: mesmas seções e rótulos do formulário, conforme Pessoa Física/Jurídica */}
            <Section title="Tipo de pessoa">
              <Field label="Tipo" value={isPJ ? 'Pessoa Jurídica' : 'Pessoa Física'} />
            </Section>
            <Section title={isPJ ? 'Dados da empresa' : 'Dados pessoais'}>
              {isPJ ? (
                <>
                  <Field label="Razão social" value={person.legal_name || person.name} />
                  <Field label="Nome fantasia" value={person.trade_name} />
                  <Field label="CNPJ" value={formatPersonDocument(person.document)} />
                </>
              ) : (
                <>
                  <Field label="Nome completo" value={person.name} />
                  <Field label="CPF" value={formatPersonDocument(person.document)} />
                </>
              )}
            </Section>
            <Section title="Contato">
              <Field label="E-mail" value={person.email} />
              <Field label="Telefone" value={person.phone ? formatPhoneForDisplay(person.phone) : null} />
            </Section>
            <Section title="Endereço">
              <Field label="CEP" value={formatCep(person.cep)} />
              <Field label="Logradouro" value={person.street} />
              <Field label="Número" value={person.address_number} />
              <Field label="Complemento" value={person.complement} />
              <Field label="Bairro" value={person.district} />
              <Field label="Cidade / UF" value={cityState} />
            </Section>
            {(person.supplier_details || person.provider_details) && (
              <Section title="Dados do papel">
                {person.supplier_details && <Field label="Inscrição estadual" value={person.supplier_details.state_registration} />}
                {person.supplier_details && <Field label="Prazo de pagamento (dias)" value={person.supplier_details.payment_term_days} />}
                {person.provider_details && <Field label="Área de atuação" value={person.provider_details.service_area} />}
              </Section>
            )}
            {person.notes && (
              <div className="mt-3 rounded-md bg-muted/40 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Observações</div>
                <p className="whitespace-pre-wrap text-sm">{person.notes}</p>
              </div>
            )}
          </TabsContent>

          {isProvider && (
            <TabsContent value="orders" className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden" forceMount>
              {/* Cards a partir dos KPIs já devolvidos pela listagem (sem nova consulta) */}
              <div className="grid shrink-0 gap-3 px-4 pt-4 sm:grid-cols-3">
                <KpiCard color="blue" icon={ClipboardList} label="Total de OS" value={kpis ? cards.total : <Loader2 className="h-4 w-4 animate-spin" />} />
                <KpiCard color="orange" icon={Wrench} label="Em andamento" value={kpis ? cards.inProgress : '—'} />
                <KpiCard color="green" icon={CheckCircle2} label="Concluídas" value={kpis ? cards.completed : '—'} />
              </div>
              <ServiceOrdersEmbeddedList providerId={person.id} onKpisChange={onKpis} />
            </TabsContent>
          )}
          {isProvider && (
            <TabsContent value="movements" className="mt-0 min-h-0 flex-1 overflow-y-auto">
              {tab === 'movements' && <ProviderMovementsTab personId={person.id} />}
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
